import React from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Crown, Globe2, Star, CreditCard, ArrowRight, Rocket } from 'lucide-react';
import { useFeaturedManagement } from '../../hooks/useFeaturedManagement';
import { CollapsibleSection } from './CollapsibleSection';
import type { ActivePlan } from './useActivePlan';

interface UpsellCard {
  key: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  body: string;
  cta: string;
  onClick: () => void;
  featured?: boolean;
}

export const UpsellSection: React.FC<{ plan: ActivePlan | null; theme?: 'light' | 'dark'; staggerIndex?: number }> = ({ plan, theme = 'dark', staggerIndex = 0 }) => {
  const isLight = theme !== 'dark';
  const navigate = useNavigate();
  const { totals } = useFeaturedManagement();

  if (!plan) return null;

  const tier = plan.membership?.tier;
  const status = plan.payment_status;
  const owesPayment = status === 'grace' || status === 'suspended';

  // Payment due — a standalone alert, not a collapsible upsell.
  if (owesPayment) {
    return (
      <button
        onClick={() => navigate('/membresia')}
        className={clsx(
          'stagger-item w-full text-left p-5 rounded-2xl border-2 flex items-center gap-4 transition-all mb-4',
          isLight ? 'bg-rose-50 border-rose-300 hover:bg-rose-100' : 'bg-rose-500/10 border-rose-500/30 hover:bg-rose-500/15'
        )}
        style={{ '--i': staggerIndex } as React.CSSProperties}
      >
        <span className="p-3 rounded-xl bg-rose-500/15 text-rose-500"><CreditCard size={22} /></span>
        <span className="flex-1">
          <span className={clsx('block font-bold', isLight ? 'text-rose-800' : 'text-rose-300')}>Tu cuenta tiene un pago pendiente</span>
          <span className="block text-sm text-slate-500">Regulariza tu membresía para no perder publicaciones ni visibilidad.</span>
        </span>
        <span className="shrink-0 flex items-center gap-1 text-rose-500 font-bold text-sm">Pagar ahora <ArrowRight size={16} /></span>
      </button>
    );
  }

  const cards: UpsellCard[] = [];

  // Manual payers → invite to switch to auto-pay. Admins only (agents don't
  // handle billing). /pagar?suscripcion=1 preselects the subscription option.
  if (plan.role === 'agency_admin' && !plan.auto_pay?.active && plan.agency_id) {
    cards.push({
      key: 'autopay', icon: CreditCard,
      title: 'Cambia a pagos automáticos',
      body: 'Actívalo una vez y olvídate de recordatorios. Cancela cuando quieras.',
      cta: 'Activar auto-pago',
      onClick: () => navigate(`/pagar?agency=${plan.agency_id}&suscripcion=1`),
    });
  }

  if (tier && tier !== 'pro' && tier !== 'cbr') {
    cards.push({
      key: 'pro', icon: Crown, featured: true,
      title: 'Pasa a Pro',
      body: 'Más publicaciones, mayor difusión en la red de Intercambio y prioridad en coincidencias.',
      cta: 'Mejorar plan',
      onClick: () => navigate('/planes?plan=pro'),
    });
  }

  const hasWebsiteAddon = (plan.addons ?? []).some(a => a.category === 'website_sync');
  if (!hasWebsiteAddon) {
    cards.push({
      key: 'web', icon: Globe2,
      title: 'Publica tu página web',
      body: 'Arma tu sitio gratis y actívalo en tu propio dominio para captar clientes con tu marca.',
      cta: 'Ver opción',
      onClick: () => navigate('/planes'),
    });
  }

  if (totals) {
    if ((totals.total_slots ?? 0) === 0) {
      cards.push({
        key: 'feat-buy', icon: Star,
        title: 'Destaca tus propiedades',
        body: 'Aparece primero en los portales y multiplica las visitas de tus mejores listados.',
        cta: 'Comprar destaque',
        onClick: () => navigate('/planes'),
      });
    } else if ((totals.total_available ?? 0) > 0) {
      cards.push({
        key: 'feat-use', icon: Star,
        title: `Tienes ${totals.total_available} destaque${totals.total_available === 1 ? '' : 's'} disponible${totals.total_available === 1 ? '' : 's'}`,
        body: 'Úsalos en tus propiedades para que aparezcan primero en los portales.',
        cta: 'Destacar ahora',
        onClick: () => navigate('/propiedades'),
      });
    }
  }

  if (cards.length === 0) return null;

  return (
    <CollapsibleSection title="Potencia tu cuenta" icon={Rocket} storageKey="dash_upsell" theme={theme} staggerIndex={staggerIndex}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div
            key={c.key}
            className={clsx(
              'flex flex-col p-5 rounded-2xl border transition-all',
              c.featured
                ? (isLight ? 'bg-white border-indigo-300 border-2 shadow-sm' : 'bg-white/5 border-2 border-indigo-500/30')
                : (isLight ? 'bg-white border-slate-100 shadow-sm hover:shadow-md' : 'bg-white/5 border-white/5 hover:border-white/10')
            )}
          >
            <span className={clsx('inline-flex p-2.5 rounded-xl mb-3 w-fit', c.featured ? 'bg-indigo-500/15 text-indigo-500' : (isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-slate-300'))}>
              <c.icon size={20} />
            </span>
            <p className={clsx('font-bold', isLight ? 'text-slate-900' : 'text-white')}>{c.title}</p>
            <p className="text-sm text-slate-500 mt-1 flex-1">{c.body}</p>
            <button
              onClick={c.onClick}
              className={clsx(
                'mt-4 w-full py-2.5 rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-1.5',
                c.featured
                  ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                  : (isLight ? 'bg-slate-100 hover:bg-slate-200 text-slate-800' : 'bg-white/10 hover:bg-white/15 text-white')
              )}
            >
              {c.cta} <ArrowRight size={15} />
            </button>
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
};
